import { useMemo } from 'react';
import helpBundle from '@sas/reacteua-novacmneua/novacmneua-gui-eua.properties';
import { addBundle, createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../../../util/testId';
import { Flow } from '../../../Layouts/Flow';
import { Select } from '../../../Select';
import { withLabels } from '../../../withLabels';
import { GlobalSettingsKeys } from '../../GlobalSettingsKeys';
import { useGlobalSettingsUIConfig } from '../../globalSettingsUIConfig';
import { useIntl } from '../../hooks/useIntl';
import { useSettingsDialogDataProvider } from '../../SettingsDialog/SettingsDialog';
import { useSettingsField } from '../../store/SettingsStore';
import bundle from '../../NovaComponents-Settings-gui-icu.properties';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SelectWithLabel = withLabels(Select);
const useTextLocalizer = createUseTextLocalizer(bundle);
addBundle(helpBundle);
export const RegionAndLanguageForm = () => {
  const formName = 'settings-global-regionAndLanguageForm';
  const [formatLocale, setFormatLocale, resetFormatLocale] = useSettingsField(GlobalSettingsKeys.FORMAT_LOCALE_KEY);
  const [offlineLocale, setOfflineLocale, resetOfflineLocale] = useSettingsField(GlobalSettingsKeys.OFFLINE_LOCALE_KEY);
  const settingsDialogDP = useSettingsDialogDataProvider();
  const hiddenSettings = settingsDialogDP.getHiddenGlobalSettings?.() ?? [];
  const isOfflineLocaleHidden = (useGlobalSettingsUIConfig(GlobalSettingsKeys.OFFLINE_LOCALE_KEY)?.hide ?? false) || hiddenSettings.includes(GlobalSettingsKeys.OFFLINE_LOCALE_KEY);
  const localize = useTextLocalizer();
  const localeInfo = useIntl();
  const listOfFormatLocales = useMemo(() => createListOfLocales('formatLocale', localize, localeInfo), [localeInfo, localize]);
  const listOfOfflineLocales = useMemo(() => createListOfLocales('offlineLocale', localize, localeInfo), [localeInfo, localize]);
  let formatLocaleIndex = 0,
    offlineLocaleIndex = 0;
  if (formatLocale) formatLocaleIndex = listOfFormatLocales.findIndex(element => formatLocale === element.id) + 1;
  if (offlineLocale) offlineLocaleIndex = listOfOfflineLocales.findIndex(element => offlineLocale === element.id) + 1;
  const offlineLocaleList = [{
    id: '',
    text: localize('regionAndLanguageForm.offlineLocaleField.default.value.label'),
    index: 0
  }, ...listOfOfflineLocales];
  const formatLocaleList = [{
    id: '',
    text: localize('regionAndLanguageForm.formatLocaleField.default.value.label'),
    index: 0
  }, ...listOfFormatLocales];
  return /*#__PURE__*/_jsxs(Flow, {
    orientation: 'vertical',
    spacing: 's',
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        width: '100%'
      },
      children: /*#__PURE__*/_jsx(SelectWithLabel, {
        testId: postfixTestId(formName, 'formatLocale'),
        label: localize('regionAndLanguageForm.formatLocaleField.label'),
        description: localize('regionAndLanguageForm.formatLocaleField.desc.label'),
        fieldMinWidth: '100%',
        fieldSuffixMinWidth: '0',
        wrapDescription: true,
        selectedIndex: formatLocaleIndex,
        onSelected: selectedIndex => {
          const index = selectedIndex ?? 0;
          index > 0 ? setFormatLocale(formatLocaleList[index].id) : resetFormatLocale();
        },
        labelHelp: {
          contentKey: 'settings.regionalFormatLocale',
          library: 'novacmneua-gui-eua',
          testId: postfixTestId(formName, 'formatLocaleHelpPopup')
        },
        values: formatLocaleList
      })
    }), !isOfflineLocaleHidden && /*#__PURE__*/_jsx("div", {
      style: {
        width: '100%'
      },
      children: /*#__PURE__*/_jsx(SelectWithLabel, {
        testId: postfixTestId(formName, 'offlineLocale'),
        label: localize('regionAndLanguageForm.offlineLocaleField.label'),
        selectedIndex: offlineLocaleIndex,
        onSelected: selectedIndex => {
          const index = selectedIndex ?? 0;
          index > 0 ? setOfflineLocale(offlineLocaleList[index].id) : resetOfflineLocale();
        },
        wrapLabel: true,
        fieldMinWidth: '100%',
        fieldSuffixMinWidth: '0',
        labelHelp: {
          contentKey: 'settings.offlineLocale',
          library: 'novacmneua-gui-eua',
          testId: postfixTestId(formName, 'offlineLocaleHelpPopup')
        },
        values: offlineLocaleList
      })
    })]
  });
};
function createListOfLocales(LocaleType, localize, localeInfo) {
  let sessionLang = localeInfo?.language;
  const localesDP = [];
  const sessionRegion = localeInfo?.region ?? null;
  const localesStr = LocaleType === 'formatLocale' ? localize('regionAndLanguageForm.format.localeList.lcl') : localize('regionAndLanguageForm.offline.localeList.lcl');
  let locales = localesStr.trim().length > 0 ? localesStr.split(',') : [];
  const listOfCustomLocales = LocaleType === 'formatLocale' ? localize('regionAndLanguageForm.format.localeOrder.lcl') : localize('regionAndLanguageForm.offline.localeOrder.lcl');
  const isCustomOrder = listOfCustomLocales && listOfCustomLocales.trim().length > 0 ? true : false;
  if (sessionLang && sessionLang.length >= 3) {
    sessionLang = sessionLang.substring(0, 2); // get the Language only
  }

  if (isCustomOrder) {
    const customLocales = listOfCustomLocales.split(',');
    if (isValidListOfLocales(customLocales, locales)) {
      locales = customLocales;
    }
  }
  for (let i = 0; i < locales.length; i++) {
    let rbLoc;
    const locale = locales[i].split('_'); // separate language from region
    const rbLang = localize(`regionAndLanguageForm.language.${locale[0].concat('.label')}`);
    const nativeLoc = nativeLocale[locales[i]];
    const retElement = {
      id: locales[i],
      text: '',
      index: i + 1
    };
    if (locale.length > 1) {
      const rbRegion = localize(`regionAndLanguageForm.region.${locale[1].concat('.label')}`);
      rbLoc = localize('regionAndLanguageForm.locales.lang_region.lcl', {
        language: rbLang,
        region: rbRegion
      });
    } else {
      rbLoc = rbLang;
    }
    if (sessionLang !== locale[0]) {
      retElement.text = localize('regionAndLanguageForm.locales.listoption.lcl', {
        rbLocale: rbLoc,
        nativeLocale: nativeLoc
      });
    } else {
      if (sessionLang === 'zh') {
        const zhScript = localeInfo?.script ?? null;
        if (typeOfScript(zhScript, sessionRegion) === typeOfScript(null, locale[1])) retElement.text = rbLoc;else retElement.text = localize('regionAndLanguageForm.locales.listoption.lcl', {
          rbLocale: rbLoc,
          nativeLocale: nativeLoc
        });
      } else retElement.text = rbLoc;
    }
    localesDP.push(retElement);
  }
  const oCollator = localeInfo?.getCollator();
  if (isCustomOrder || !oCollator) {
    localesDP.sort(function (str1, str2) {
      return str1.index > str2.index ? 1 : -1;
    });
  } else {
    localesDP.sort(function (str1, str2) {
      return oCollator.compare(str1.text, str2.text);
    });
  }
  return localesDP;
}
function isValidListOfLocales(customLocales, locales) {
  let isValid = true;
  locales.forEach(function (locId) {
    const index = customLocales.indexOf(locId);
    if (index < 0) {
      isValid = false;
    } else {
      customLocales.splice(index, 1);
    }
  });
  if (isValid && customLocales.length !== 0) {
    isValid = false;
  }
  return isValid;
}
function typeOfScript(script, region) {
  if (script !== null && script === 'Hant') return 1;
  if (region !== null && (region === 'TW' || region === 'HK' || region === 'MO')) return 1;
  return 0;
}
// TODO : As the below text is non-translatable including like this. Once the feature for including non-translatable text is done we can move this.
/* eslint-disable @sas/i18n/jsx-to-localize */
const nativeLocale = {
  ar: 'العربية',
  he: 'עברית',
  af_ZA: 'Afrikaans (Suid-Afrika)‎',
  ar_AE: 'العربية (الإمارات العربية المتحدة)‏',
  ar_BH: 'العربية (البحرين)‏',
  ar_DZ: 'العربية (الجزائر)‏',
  ar_EG: 'العربية (مصر)‏',
  ar_IN: 'العربية (الهند)‏',
  ar_IQ: 'العربية (العراق)‏',
  ar_JO: 'العربية (الأردن)‏',
  ar_KW: 'العربية (الكويت)‏',
  ar_LB: 'العربية (لبنان)‏',
  ar_LY: 'العربية (ليبيا)‏',
  ar_MA: 'العربية (المغرب)‏',
  ar_OM: 'العربية (عُمان)‏',
  ar_QA: 'العربية (قطر)‏',
  ar_SA: 'العربية (المملكة العربية السعودية)‏',
  ar_SD: 'العربية (السودان)‏',
  ar_SY: 'العربية (سوريا)‏',
  ar_TN: 'العربية (تونس)‏',
  ar_YE: 'العربية (اليمن)‏',
  be_BY: 'Беларуская (Беларусь)',
  bg_BG: 'Български (България)',
  bn_IN: 'বাংলা (ভারত)',
  bs_BA: 'Bosanski (Bosna i Hercegovina)‎',
  ca_ES: 'Català (Espanya)‎',
  cs_CZ: 'Čeština (Česko)‎',
  da_DK: 'Dansk (Danmark)‎',
  de_AT: 'Deutsch (Österreich)‎',
  de_CH: 'Deutsch (Schweiz)‎',
  de_DE: 'Deutsch (Deutschland)‎',
  de_LI: 'Deutsch (Liechtenstein)‎',
  de_LU: 'Deutsch (Luxemburg)‎',
  el_CY: 'Ελληνικά (Κύπρος)‎',
  el_GR: 'Ελληνικά (Ελλάδα)‎',
  en_AU: 'English (Australia)‎',
  en_BE: 'English (Belgium)‎',
  en_BW: 'English (Botswana)‎',
  en_CA: 'English (Canada)‎',
  en_CB: 'English (Caribbean)‎',
  en_GB: 'English (United Kingdom)‎',
  en_HK: 'English (Hong Kong SAR China)‎',
  en_IE: 'English (Ireland)‎',
  en_IN: 'English (India)‎',
  en_JM: 'English (Jamaica)‎',
  en_MT: 'English (Malta)‎',
  en_NZ: 'English (New Zealand)‎',
  en_PH: 'English (Philippines)‎',
  en_SG: 'English (Singapore)‎',
  en_US: 'English (United States)‎',
  en_ZA: 'English (South Africa)‎',
  en_ZW: 'English (Zimbabwe)‎',
  es_AR: 'Español (Argentina)‎',
  es_BO: 'Español (Bolivia)‎',
  es_CL: 'Español (Chile)‎',
  es_CO: 'Español (Colombia)‎',
  es_CR: 'Español (Costa Rica)‎',
  es_DO: 'Español (República Dominicana)‎',
  es_EC: 'Español (Ecuador)‎',
  es_ES: 'Español (España)‎',
  es_GT: 'Español (Guatemala)‎',
  es_HN: 'Español (Honduras)‎',
  es_MX: 'Español (México)‎',
  es_NI: 'Español (Nicaragua)‎',
  es_PA: 'Español (Panamá)‎',
  es_PE: 'Español (Perú)‎',
  es_PR: 'Español (Puerto Rico)‎',
  es_PY: 'Español (Paraguay)‎',
  es_SV: 'Español (El Salvador)‎',
  es_US: 'Español (Estados Unidos)‎',
  es_UY: 'Español (Uruguay)‎',
  es_VE: 'Español (Venezuela)‎',
  et_EE: 'Eesti (Eesti)‎',
  eu_ES: 'Euskara (Espainia)‎',
  fa_IN: 'فارسی (هند)‏',
  fa_IR: 'فارسی (ایران)‏',
  fi_FI: 'Suomi (Suomi)‎',
  fo_FO: 'Føroyskt (Føroyar)‎',
  fr_BE: 'Français (Belgique)‎',
  fr_CA: 'Français (Canada)‎',
  fr_CH: 'Français (Suisse)‎',
  fr_FR: 'Français (France)‎',
  fr_LU: 'Français (Luxembourg)‎',
  ga_IE: 'Gaeilge (Éire)‎',
  he_IL: 'עברית (ישראל)‏',
  hi_IN: 'हिन्दी (भारत)‎',
  hr_BA: 'Hrvatski (Bosna i Hercegovina)‎',
  hr_HR: 'Hrvatski (Hrvatska)‎',
  hu_HU: 'Magyar (Magyarország)‎',
  id_ID: 'Indonesia (Indonesia)‎',
  is_IS: 'Íslenska (Ísland)‎',
  it_CH: 'Italiano (Svizzera)‎',
  it_IT: 'Italiano (Italia)‎',
  ja_JP: '日本語 (日本)‎',
  kk_KZ: 'Қазақ тілі (Қазақстан)‎',
  kl_GL: 'Kalaallisut (Kalaallit Nunaat)‎',
  ko_KR: '한국어 (대한민국)‎',
  kw_GB: 'Kernewek (Rywvaneth Unys)‎',
  lt_LT: 'Lietuvių (Lietuva)‎',
  lv_LV: 'Latviešu (Latvija)‎',
  mk_MK: 'Македонски (Македонија)‎',
  mr_IN: 'मराठी (भारत)‎',
  ms_MY: 'Bahasa Melayu (Malaysia)‎',
  mt_MT: 'Malti (Malta)‎',
  nb_NO: 'Norsk bokmål (Norge)‎',
  nl_BE: 'Nederlands (België)‎',
  nl_NL: 'Nederlands (Nederland)‎',
  nn_NO: 'Nynorsk (Noreg)‎',
  no_NO: 'Norsk (Noreg)‎',
  pl_PL: 'Polski (Polska)‎',
  pt_BR: 'Português (Brasil)‎',
  pt_PT: 'Português (Portugal)‎',
  ro_RO: 'Română (România)‎',
  ru_RU: 'Русский (Россия)‎',
  ru_UA: 'Русский (Украина)‎',
  sh_BA: 'Srpskohrvatski (Bosna i Hercegovina)‎',
  sh_ME: 'Srpskohrvatski (Crna Gora)‎',
  sh_RS: 'Srpskohrvatski (Srbija)‎',
  sk_SK: 'Slovenčina (Slovensko)‎',
  sl_SI: 'Slovenščina (Slovenija)‎',
  sq_AL: 'Shqip (Shqipëri)‎',
  sr_BA: 'Српски (Босна и Херцеговина)‎',
  sr_ME: 'Српски (Црна Гора)‎',
  sr_RS: 'Српски (Србија)‎',
  sv_SE: 'Svenska (Sverige)‎',
  ta_IN: 'தமிழ் (இந்தியா)‎',
  te_IN: 'తెలుగు (భారత దేశం)‎',
  th_TH: 'ไทย (ไทย)‎',
  tl_PH: 'Tagalog (Pilipinas)‎',
  tr_TR: 'Türkçe (Türkiye)‎',
  uk_UA: 'Українська (Україна)‎',
  vi_VN: 'Tiếng Việt (Việt Nam)‎',
  zh_CN: '中文 (中国)‎',
  zh_HK: '中文 (中國香港特別行政區)‎',
  zh_MO: '中文 (中國澳門特別行政區)‎',
  zh_SG: '中文 (新加坡)‎',
  zh_TW: '中文 (台灣)‎'
};
/* eslint-enable @sas/i18n/jsx-to-localize */
//# sourceMappingURL=RegionAndLanguageForm.js.map